'use client';

import { createContext, createElement, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import type { CapRouterOutlet } from '@capgo/capacitor-transitions';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { useCapacitor } from '../../_lib/capacitor/use-capacitor';

import { createNativeNavigationRuntime, restoreNativeScroll, type NativeNavigationRuntime } from './_lib/native-navigation-runtime';
import styles from './native-navigation.module.css';

const NavigationContext = createContext<{ current: NativeNavigationRuntime | null } | null>(null);
const TabBarContainerContext = createContext<HTMLDivElement | null>(null);
const OverlayContainerContext = createContext<HTMLDivElement | null>(null);

export function useNativeTabBarContainer() {
  return useContext(TabBarContainerContext);
}

/**
 * Host for chrome that must paint ABOVE the tab bar — the nav drawer and its
 * scrim. The route outlet sets `isolation: isolate`, so anything rendered
 * inside a page gets its own stacking context and can never outrank the tab
 * bar portalled alongside it, whatever its z-index. This host is a sibling
 * declared AFTER the tab bar host, so paint order settles it.
 */
export function useNativeOverlayContainer() {
  return useContext(OverlayContainerContext);
}

export function NativeNavigationBoundary({ children }: { children: React.ReactNode }) {
  const { isNative } = useCapacitor();
  const [tabBarContainer, setTabBarContainer] = useState<HTMLDivElement | null>(null);
  const [overlayContainer, setOverlayContainer] = useState<HTMLDivElement | null>(null);

  if (!isNative) return children;

  return (
    <NativeNavigationProvider>
      <TabBarContainerContext.Provider value={tabBarContainer}>
        <OverlayContainerContext.Provider value={overlayContainer}>
          <NativeNavigationIdentity />
          <NativeRouteViewport>{children}</NativeRouteViewport>
          {/* Keep app tabs outside the moving, retained route snapshots. */}
          <div ref={setTabBarContainer} data-native-tab-bar-host="" />
          {/* Declared last so drawer chrome paints over the tab bar. */}
          <div ref={setOverlayContainer} data-native-overlay-host="" />
        </OverlayContainerContext.Provider>
      </TabBarContainerContext.Provider>
    </NativeNavigationProvider>
  );
}

function NativeNavigationProvider({ children }: { children: React.ReactNode }) {
  const runtime = useRef<NativeNavigationRuntime | null>(null);

  // One runtime survives route-group changes between customer pages and auth.
  useLayoutEffect(() => {
    runtime.current = createNativeNavigationRuntime();
    return () => {
      runtime.current?.destroy();
      runtime.current = null;
    };
  }, []);

  return <NavigationContext.Provider value={runtime}>{children}</NavigationContext.Provider>;
}

function NativeNavigationIdentity() {
  const runtime = useContext(NavigationContext);
  const client = useSupabase();
  // Discard retained screens when the signed-in identity changes.
  useEffect(() => {
    const { data } = client.auth.onAuthStateChange((_event, session) => {
      runtime?.current?.resetScope(session?.user.id ?? 'anonymous', 'user');
    });
    return () => data.subscription.unsubscribe();
  }, [client, runtime]);

  return null;
}

export function useNativeNavigationScope(tenantId?: string) {
  const runtime = useContext(NavigationContext);

  // Tenant/auth layouts configure the shared runtime without replacing its outlet.
  useEffect(() => {
    if (tenantId) runtime?.current?.resetScope(tenantId, 'tenant');
  }, [runtime, tenantId]);
}

export function useNativeCanGoBack() {
  const runtime = useContext(NavigationContext);
  return useSyncExternalStore(
    (listener) => runtime?.current?.subscribe(listener) ?? (() => {}),
    () => runtime?.current?.history.canGoBack ?? false,
    () => false,
  );
}

export function useNativeBack() {
  const runtime = useContext(NavigationContext);
  const router = useRouter();
  return () => {
    if (runtime?.current) runtime.current.back((href) => router.replace(href));
    else router.back();
  };
}

function NativeRouteViewport({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const runtime = useContext(NavigationContext);
  const outlet = useRef<CapRouterOutlet | null>(null);

  // Custom elements access HTMLElement on import, so load them only in the browser.
  useEffect(() => {
    let active = true;
    void import('@capgo/capacitor-transitions').then(() => {
      if (active && outlet.current) runtime?.current?.attachOutlet(outlet.current);
    }).catch((error: unknown) => {
      console.error('Unable to load native page transitions', error);
    });
    return () => { active = false; };
  }, [runtime]);

  return createElement('cap-router-outlet', {
    ref: outlet,
    className: styles.outlet,
    platform: 'ios',
    duration: '350',
    'max-cached': '4',
    'swipe-gesture': 'false',
    'data-test': 'native-route-outlet',
  }, <NativeRoutePage key={pathname}>{children}</NativeRoutePage>);
}

function NativeRoutePage({ children }: { children: React.ReactNode }) {
  const runtime = useContext(NavigationContext);
  const page = useRef<HTMLDivElement>(null);

  // Retain the outgoing screen as a DOM snapshot while React mounts the next route.
  useEffect(() => {
    const element = page.current;
    if (!element) return;
    const navigation = runtime?.current;
    navigation?.attachPage(element);
    const stopRestoring = restoreNativeScroll(element, navigation?.history.current?.scrollTop ?? 0, location.hash);
    return () => {
      stopRestoring();
      navigation?.detachPage(element);
    };
  }, [runtime]);

  return <div ref={page} className={styles.page} data-native-route-page="" onScroll={(event) => runtime?.current?.saveScroll(event.currentTarget)}>
    <div className={styles.content}>{children}</div>
  </div>;
}
